// ABM List (Signals) — Excel lead import + table view
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, History, RefreshCw, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FilterSelect } from "@/components/common/FilterSelect";
import { signalsApi, outreachApi, downloadCustomLeadsTemplate } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { applyJourneyToAbmLead } from "@/lib/abmLead";
import { statusColor, flattenWorkflow } from "@/pages/signals/rnc/rncData";
import {
  SEARCH_DEBOUNCE_MS,
  getAbmEmptyMessage,
  resolveDebouncedQuery,
  shouldApplyLoadResponse,
} from "@/pages/signals/customLeadsSearch";
import { AbmImportHistoryModal } from "@/pages/signals/AbmImportHistoryModal";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

function formatImportFailure(result) {
  const errs = result?.errors;
  if (Array.isArray(errs) && errs.length > 0) {
    const first = errs[0];
    const rowPrefix = first.row ? `Row ${first.row}: ` : "";
    return `${rowPrefix}${first.message || "Validation failed"}`;
  }
  const fail = result?.failedCount || 0;
  if (fail > 0) {
    return `${fail} row${fail === 1 ? "" : "s"} failed validation`;
  }
  return "No leads imported";
}

function getBarColor(statusCol) {
  if (statusCol === "green") return "#16A34A";
  if (statusCol === "red") return "#DC2626";
  if (statusCol === "amber") return "#D97706";
  return "#94A3B8";
}

function _notifyImportResult(result) {
  const ok = result.successCount || 0;
  const fail = result.failedCount || 0;
  const skipped = result.skippedCount || 0;
  if (ok > 0 && fail === 0 && skipped === 0) {
    toast.success(`Imported ${ok} lead${ok === 1 ? "" : "s"}`);
  } else if (ok > 0) {
    toast.message(
      `Imported ${ok} lead(s)`
      + (skipped ? `; ${skipped} duplicate(s) skipped` : "")
      + (fail ? `; ${fail} failed validation` : "")
    );
  } else if (skipped > 0 && fail === 0) {
    toast.message(`No new leads — ${skipped} duplicate(s) skipped`);
  } else if (fail > 0) {
    toast.error(formatImportFailure(result));
  } else {
    toast.error("No leads imported");
  }
}



function getStepLabel(lead, flatSteps, workflowAssigned) {
  if (lead.currentStep) return lead.currentStep;
  if (workflowAssigned && flatSteps?.[lead.stepIndex]?.title) {
    return flatSteps[lead.stepIndex].title;
  }
  if (!workflowAssigned) return "Pending Workflow Assignment";
  return "N/A";
}

function LeadsTable({
  loading,
  refreshing,
  error,
  leads,
  emptyMessage,
  deletingId,
  bulkDeleting,
  selectedIds,
  onToggleSelect,
  onSelectAllOnPage,
  allOnPageSelected,
  someOnPageSelected,
  onDelete,
  onViewDetails,
}) {
  const showBlockingLoad = loading && leads.length === 0;
  const selectionDisabled = Boolean(deletingId) || bulkDeleting;
  const selectAllState = allOnPageSelected ? true : (someOnPageSelected ? "indeterminate" : false);
  return (
    <div
      className={`card rnc-table-card${refreshing ? " is-refreshing" : ""}`}
      data-testid="custom-leads-table"
      aria-busy={refreshing || undefined}
    >
      <p className="rnc-table-scroll-hint" aria-hidden="true">Swipe table to see more columns</p>
      <div className="rnc-table-wrap">
        <table className="rnc-data-table abm-leads-table">
          <thead>
            <tr>
              <th className="abm-leads-select-col">
                <Checkbox
                  checked={selectAllState}
                  onCheckedChange={onSelectAllOnPage}
                  disabled={selectionDisabled || leads.length === 0}
                  className="h-3.5 w-3.5"
                  aria-label="Select all leads on this page"
                  data-testid="custom-leads-select-all"
                />
              </th>
              <th>COMPANY</th>
              <th>CAMPAIGN</th>
              <th>CURRENT STEP</th>
              <th>STATUS</th>
              <th>PROGRESS</th>
              <th>LAST ACTIVITY</th>
              <th>IMPORTED</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {showBlockingLoad && (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: 40, color: "var(--slate-400)" }}>
                  Loading ABM List leads…
                </td>
              </tr>
            )}
            {!showBlockingLoad && error && leads.length === 0 && (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: 40, color: "#b91c1c" }}>
                  {error}
                </td>
              </tr>
            )}
            {!showBlockingLoad && !error && leads.map((lead) => {
              const sc = lead.status ? statusColor(lead.status) : "slate";
              const barColor = getBarColor(sc);
              const workflowAssigned = Boolean(lead.workflowAssigned);
              const flat = flattenWorkflow(lead.signalWorkflow);
              const curStepLabel = getStepLabel(lead, flat, workflowAssigned);
              const progressPct = lead.progress == null ? null : Math.round(Number(lead.progress) * 100);
              const isDeleting = deletingId === lead.id;
              const isSelected = selectedIds.has(lead.id);
              return (
                <tr key={lead.id} data-testid={`custom-lead-row-${lead.id}`}>
                  <td className="abm-leads-select-col">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect(lead.id)}
                      disabled={selectionDisabled}
                      className="h-3.5 w-3.5"
                      aria-label={`Select ${lead.contact || lead.company}`}
                      data-testid={`custom-lead-select-${lead.id}`}
                    />
                  </td>
                  <td>
                    <div className="rowcompany">
                      <div className="mk" style={{ background: lead.color }}>
                        {lead.initials}
                      </div>
                      <div>
                        <div className="nm">{lead.company}</div>
                        <div className="sub">{lead.location || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 13 }}>{lead.campaignName || "N/A"}</td>
                  <td style={{ fontSize: 13 }}>{curStepLabel}</td>
                  <td>
                    {lead.status ? (
                      <span className={`status-pill badge ${sc}`}><span className="dotb" />{lead.status}</span>
                    ) : (
                      <span style={{ color: "var(--slate-400)", fontSize: 13 }}>N/A</span>
                    )}
                  </td>
                  <td>
                    {progressPct == null ? (
                      <span style={{ color: "var(--slate-400)", fontSize: 13 }}>N/A</span>
                    ) : (
                      <div className="progress-cell">
                        <div className="bar"><div style={{ width: `${progressPct}%`, background: barColor }} /></div>
                        <span>{progressPct}%</span>
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 13 }}>{formatDateTime(lead.lastActivity, "N/A")}</td>
                  <td style={{ fontSize: 13 }}>{formatDateTime(lead.dateAdded || lead.uploadedAt)}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-start" }}>
                      <button
                        type="button"
                        className="view-details-btn"
                        onClick={() => onViewDetails(lead.id)}
                        data-testid={`view-abm-${lead.id}`}
                      >
                        View Details
                      </button>
                      <button
                        type="button"
                        className="btn"
                        title="Delete lead"
                        disabled={isDeleting}
                        onClick={() => onDelete(lead)}
                        data-testid={`custom-lead-delete-${lead.id}`}
                        aria-label={`Delete ${lead.contact || lead.company}`}
                        style={{ padding: "4px 8px", color: "#b91c1c", borderColor: "transparent" }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!showBlockingLoad && !refreshing && !error && leads.length === 0 && (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: 40, color: "var(--slate-400)" }}>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

LeadsTable.propTypes = {
  loading: PropTypes.bool,
  refreshing: PropTypes.bool,
  error: PropTypes.string,
  leads: PropTypes.array,
  emptyMessage: PropTypes.string,
  deletingId: PropTypes.string,
  bulkDeleting: PropTypes.bool,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onSelectAllOnPage: PropTypes.func.isRequired,
  allOnPageSelected: PropTypes.bool,
  someOnPageSelected: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

export default function CustomLeadsPanel({ refreshToken = 0 }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const queryRef = useRef(query);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [leads, setLeads] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [total, setTotal] = useState(0);
  const [imports, setImports] = useState([]);
  const [lastSummary, setLastSummary] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [importHistoryOpen, setImportHistoryOpen] = useState(false);
  const loadRequestIdRef = useRef(0);
  const searchDebounceRef = useRef(null);

  queryRef.current = query;

  const commitSearchQuery = useCallback((rawValue) => {
    const next = resolveDebouncedQuery(rawValue, queryRef.current);
    if (next === null) return;
    queryRef.current = next;
    setQuery(next);
    setPage(0);
  }, []);

  const flushSearch = useCallback((rawValue = search) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    commitSearchQuery(rawValue);
  }, [commitSearchQuery, search]);

  useEffect(() => {
    searchDebounceRef.current = setTimeout(() => {
      commitSearchQuery(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [search, commitSearchQuery]);

async function _fetchJourneysForLeads(rawLeads) {
  const leadIds = rawLeads.map((l) => l.id).filter(Boolean);
  if (!leadIds.length) return {};
  try {
    const batch = await outreachApi.listAbmJourneys(leadIds);
    return batch?.journeys || {};
  } catch {
    return {};
  }
}

  const fetchLeads = useCallback(async (fetchQuery, fetchPage, fetchPageSize) => {
    const requestId = ++loadRequestIdRef.current;
    const isInitialLoad = !hasLoaded;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    if (isInitialLoad) setError(null);

    try {
      const data = await signalsApi.listCustomLeads({
        q: fetchQuery || undefined,
        limit: fetchPageSize,
        offset: fetchPage * fetchPageSize,
      });
      if (!shouldApplyLoadResponse(requestId, loadRequestIdRef.current)) return;

      const rawLeads = data.leads || [];
      const journeys = await _fetchJourneysForLeads(rawLeads);
      const enriched = rawLeads.map((lead, index) => (
        applyJourneyToAbmLead(lead, journeys[lead.id], index)
      ));

      setLeads(enriched);
      setTotal(data.total || 0);
      setImports(data.imports || []);
      setError(null);
      setHasLoaded(true);
    } catch (e) {
      if (!shouldApplyLoadResponse(requestId, loadRequestIdRef.current)) return;

      const message = e.message || "Could not load ABM List leads";
      if (isInitialLoad) {
        setError(message);
        setLeads([]);
        setTotal(0);
      } else {
        toast.error(message);
      }
    } finally {
      if (shouldApplyLoadResponse(requestId, loadRequestIdRef.current)) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [hasLoaded]);

  const load = useCallback(() => fetchLeads(query, page, pageSize), [fetchLeads, query, page, pageSize]);

  useEffect(() => {
    fetchLeads(query, page, pageSize);
  }, [fetchLeads, query, page, pageSize, refreshToken]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, query]);

  const pageLeadIds = useMemo(() => leads.map((lead) => lead.id).filter(Boolean), [leads]);
  const allOnPageSelected = pageLeadIds.length > 0 && pageLeadIds.every((id) => selectedIds.has(id));
  const someOnPageSelected = pageLeadIds.some((id) => selectedIds.has(id));

  const toggleSelect = useCallback((leadId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }, []);

  const selectAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      if (allOnPageSelected) {
        const next = new Set(prev);
        pageLeadIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      pageLeadIds.forEach((id) => next.add(id));
      return next;
    });
  }, [allOnPageSelected, pageLeadIds]);

  const emptyMessage = getAbmEmptyMessage({
    hasLoaded,
    query,
    hasAnyLeadsInWorkspace: total > 0,
  });

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const rangeFrom = total === 0 ? 0 : page * pageSize + 1;
  const rangeTo = page * pageSize + leads.length;
  const onPageSizeChange = (nextRaw) => {
    const next = Number(nextRaw);
    if (!PAGE_SIZE_OPTIONS.includes(next)) return;
    setPageSize(next);
    setPage(0);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadCustomLeadsTemplate();
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.message || "Failed to download template");
    }
  };

  const handleDeleteLead = (lead) => {
    setDeleteTarget(lead);
  };

  const confirmDeleteLead = async () => {
    if (!deleteTarget) return;
    const lead = deleteTarget;
    setDeletingId(lead.id);
    try {
      await signalsApi.deleteCustomLead(lead.id);
      toast.success("Lead removed");
      setDeleteTarget(null);
      setSelectedIds((prev) => {
        if (!prev.has(lead.id)) return prev;
        const next = new Set(prev);
        next.delete(lead.id);
        return next;
      });
      if (leads.length === 1 && page > 0) {
        setPage((p) => Math.max(0, p - 1));
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e.message || "Could not delete lead");
    } finally {
      setDeletingId(null);
    }
  };

  const confirmBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const result = await signalsApi.deleteCustomLeadsBulk(ids);
      const deleted = result.deleted || 0;
      if (deleted > 0) {
        toast.success(`Removed ${deleted} lead${deleted === 1 ? "" : "s"}`);
      }
      if (result.notFound?.length) {
        toast.message(`${result.notFound.length} lead(s) were already removed`);
      }
      setBulkDeleteOpen(false);
      setSelectedIds(new Set());
      const remainingOnPage = leads.length - deleted;
      if (remainingOnPage <= 0 && page > 0) {
        setPage((p) => Math.max(0, p - 1));
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e.message || "Could not delete selected leads");
    } finally {
      setBulkDeleting(false);
    }
  };

  const deleteTargetLabel = deleteTarget
    ? (deleteTarget.contact || deleteTarget.company || deleteTarget.contactName || deleteTarget.companyName || "this lead")
    : "";

  const handleUpload = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      toast.error("Only .xlsx and .xls files are supported");
      return;
    }
    setUploading(true);
    try {
      const result = await signalsApi.importCustomLeads(file);
      setLastSummary(result);
      _notifyImportResult(result);
      setPage(0);
      await load();
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid="signals-custom-panel">
      <div className="filters" style={{ marginBottom: 0, flexWrap: "wrap" }}>
        <input
          placeholder="Search company, contact, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") flushSearch(search);
          }}
          data-testid="custom-leads-search"
        />
        <button
          type="button"
          className="btn"
          onClick={handleDownloadTemplate}
          data-testid="custom-leads-download-template"
        >
          <Download size={14} /> Download Template
        </button>
        <button
          type="button"
          className="btn"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          data-testid="custom-leads-upload-btn"
        >
          <Upload size={14} /> {uploading ? "Importing…" : "Upload Excel"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: "none" }}
          data-testid="custom-leads-file-input"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
        <button
          type="button"
          className="btn"
          onClick={() => setImportHistoryOpen(true)}
          data-testid="custom-leads-import-history-btn"
        >
          <History size={14} /> Import history
          {imports.length > 0 ? ` (${imports.length})` : ""}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => load()}
          data-testid="custom-leads-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {lastSummary && (
        <div className="card abm-import-summary" data-testid="custom-leads-import-summary">
          <div className="abm-import-summary-header">
            <span className="abm-import-summary-label">Last import</span>
            <span className="abm-import-summary-file" title={lastSummary.fileName}>
              {lastSummary.fileName}
            </span>
          </div>
          <dl className="abm-import-summary-stats">
            <div>
              <dt>Imported</dt>
              <dd>{lastSummary.successCount ?? 0}</dd>
            </div>
            {(lastSummary.duplicateFileCount || lastSummary.duplicateDbCount) ? (
              <div>
                <dt>Skipped duplicates</dt>
                <dd>{(lastSummary.duplicateFileCount || 0) + (lastSummary.duplicateDbCount || 0)}</dd>
              </div>
            ) : null}
            {lastSummary.failedCount ? (
              <div>
                <dt>Failed validation</dt>
                <dd className="abm-import-stat--failed">{lastSummary.failedCount}</dd>
              </div>
            ) : null}
            {lastSummary.skippedCount ? (
              <div>
                <dt>Skipped</dt>
                <dd>{lastSummary.skippedCount}</dd>
              </div>
            ) : null}
            <div>
              <dt>Rows processed</dt>
              <dd>{lastSummary.totalRows ?? 0}</dd>
            </div>
          </dl>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div
          className="abm-bulk-actions"
          data-testid="custom-leads-bulk-actions"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            padding: "8px 12px",
            borderRadius: 8,
            background: "var(--slate-50)",
            border: "1px solid var(--slate-200)",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--slate-700)" }}>
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            className="btn"
            disabled={bulkDeleting}
            onClick={() => setSelectedIds(new Set())}
            data-testid="custom-leads-clear-selection"
          >
            Clear selection
          </button>
          <button
            type="button"
            className="btn"
            disabled={bulkDeleting}
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="custom-leads-bulk-delete-btn"
            style={{ color: "#b91c1c", borderColor: "#fecaca" }}
          >
            <Trash2 size={14} /> Delete selected
          </button>
        </div>
      )}

      <LeadsTable
        loading={loading}
        refreshing={refreshing}
        error={error}
        leads={leads}
        emptyMessage={emptyMessage}
        deletingId={deletingId}
        bulkDeleting={bulkDeleting}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onSelectAllOnPage={selectAllOnPage}
        allOnPageSelected={allOnPageSelected}
        someOnPageSelected={someOnPageSelected}
        onDelete={handleDeleteLead}
        onViewDetails={(leadId) => navigate(`/signals/abm/${encodeURIComponent(leadId)}`)}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} className="custom-leads-pagination">
        <p style={{ color: "var(--slate-500)", fontSize: 13.5, margin: 0 }}>
          Showing {rangeFrom}{"–"}{rangeTo} of {total} ABM List leads
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--slate-500)" }}>
            Per page
            <FilterSelect
              variant="field"
              value={pageSize}
              onChange={onPageSizeChange}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
              className="w-auto"
              triggerClassName="h-9 w-auto min-w-[52px] px-2.5"
              data-testid="custom-leads-page-size"
              aria-label="Records per page"
            />
          </label>
          <button
            type="button"
            className="btn"
            disabled={page <= 0 || loading || refreshing}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            data-testid="custom-leads-page-prev"
          >
            Previous
          </button>
          <span style={{ fontSize: 13, color: "var(--slate-500)", alignSelf: "center" }}>
            Page {Math.min(page + 1, pageCount)} / {pageCount}
          </span>
          <button
            type="button"
            className="btn"
            disabled={loading || refreshing || page + 1 >= pageCount || total === 0}
            onClick={() => setPage((p) => p + 1)}
            data-testid="custom-leads-page-next"
          >
            Next
          </button>
        </div>
      </div>

      <AbmImportHistoryModal
        open={importHistoryOpen}
        onOpenChange={setImportHistoryOpen}
        imports={imports}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deletingId) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent data-testid="abm-delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from ABM List?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTargetLabel
                ? `Remove ${deleteTargetLabel} from the ABM List?`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingId)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              disabled={Boolean(deletingId)}
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteLead();
              }}
            >
              {deletingId ? "Removing…" : "Remove lead"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={bulkDeleteOpen}
        onOpenChange={(open) => {
          if (!open && !bulkDeleting) setBulkDeleteOpen(false);
        }}
      >
        <AlertDialogContent data-testid="abm-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove selected leads?</AlertDialogTitle>
            <AlertDialogDescription>
              {`Remove ${selectedIds.size} lead${selectedIds.size === 1 ? "" : "s"} from the ABM List? This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              disabled={bulkDeleting}
              onClick={(e) => {
                e.preventDefault();
                confirmBulkDelete();
              }}
            >
              {bulkDeleting ? "Removing…" : `Remove ${selectedIds.size} lead${selectedIds.size === 1 ? "" : "s"}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

CustomLeadsPanel.propTypes = {
  refreshToken: PropTypes.number,
};
